import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../../hooks/usePageTitle'
import { assignmentRulesApi, slaPoliciesApi, customFieldsApi, savedViewsApi } from '../../api/settings'

/** Render an SLA-style minute count as the smallest readable unit. */
function formatDurationMinutes(min: number): string {
  if (min < 60) return `${min} min`
  if (min < 60 * 24) return `${(min / 60).toFixed(min % 60 === 0 ? 0 : 1)}h`
  return `${(min / 60 / 24).toFixed(min % (60 * 24) === 0 ? 0 : 1)}d`
}
import { PageSpinner } from '../../components/ui/Spinner'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { formatDate } from '../../lib/utils'
import { DropdownSelect } from '../../components/ui/DropdownSelect'

// ─── Assignment Rules ────────────────────────────────────────────────────────
export function AssignmentRulesSettings() {
  usePageTitle('Assignment Rules')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)
  // Default true on the admin screen — without this, disabling a rule made
  // it disappear from the list with no way to re-enable (Notion #67).
  const [includeInactive, setIncludeInactive] = useState(true)

  const { data, isLoading } = useQuery({
    queryKey: ['assignment-rules', companyId, includeInactive],
    queryFn: () => assignmentRulesApi.list(companyId!, includeInactive).then(r => r.data),
    enabled: !!companyId,
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      assignmentRulesApi.update(companyId!, id, { isActive }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignment-rules', companyId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => assignmentRulesApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Rule deleted')
      qc.invalidateQueries({ queryKey: ['assignment-rules', companyId] })
      setDeleteTarget(null)
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Assignment Rules</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={e => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm"><Plus className="w-3.5 h-3.5" /> Add Rule</button>
        </div>
      </div>
      <div className="card overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Applies To</th><th>Method</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {(!data || data.length === 0) && (
              <tr><td colSpan={5} className="text-center py-8 text-gray-500 text-sm">
                {includeInactive ? 'No rules' : 'No active rules. Toggle "Show inactive" to see disabled ones.'}
              </td></tr>
            )}
            {data?.map((r: { id: string; name: string; applyTo: string; method: string; isActive: boolean }) => (
              <tr key={r.id}>
                <td className="font-medium">{r.name}</td>
                <td><span className="badge badge-gray capitalize">{r.applyTo}</span></td>
                <td><span className="badge badge-blue">{r.method.replace(/_/g, ' ')}</span></td>
                <td><span className={`badge ${r.isActive ? 'badge-green' : 'badge-gray'}`}>{r.isActive ? 'Active' : 'Off'}</span></td>
                <td className="flex gap-1">
                  <button
                    onClick={() => toggleMutation.mutate({ id: r.id, isActive: !r.isActive })}
                    disabled={toggleMutation.isPending}
                    className="btn btn-ghost btn-sm text-xs"
                  >
                    {r.isActive ? 'Disable' : 'Enable'}
                  </button>
                  <button onClick={() => setDeleteTarget({ id: r.id, label: r.name })} className="btn btn-ghost btn-sm p-1 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add Assignment Rule" size="md">
        <AssignmentRuleForm companyId={companyId!} onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['assignment-rules', companyId] }) }} />
      </Modal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete assignment rule?"
        message={`This will remove "${deleteTarget?.label}". Future leads/deals will skip this rule when matching.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

function AssignmentRuleForm({ companyId, onSuccess }: { companyId: string; onSuccess: () => void }) {
  const { register, handleSubmit, reset, watch, setValue } = useForm<{ name: string; applyTo: string; method: string; assignees?: string }>({
    defaultValues: { applyTo: 'lead', method: 'round_robin' },
  })
  const mutation = useMutation({
    mutationFn: (dto: { name: string; applyTo: string; method: string; assignees?: string }) => {
      const payload = {
        name: dto.name,
        applyTo: dto.applyTo,
        method: dto.method,
        assignees: dto.assignees ? dto.assignees.split(',').map(s => s.trim()).filter(Boolean) : [],
        // Conditions is a recursive AND/OR tree on the backend (not an array).
        // v1 ships an empty object — rule applies to ALL records of applyTo.
        // A condition-tree builder UI is its own future feature.
        conditions: {},
      }
      return assignmentRulesApi.create(companyId, payload)
    },
    onSuccess: () => { reset(); toast.success('Rule created'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to create rule')
    },
  })
  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div><label className="label">Rule Name *</label><input {...register('name', { required: true })} className="input" /></div>
      <div>
        <label className="label">Applies To</label>
        <DropdownSelect
          value={watch('applyTo') ?? 'lead'}
          onChange={v => setValue('applyTo', v)}
          options={[{ value: 'lead', label: 'Lead' }, { value: 'deal', label: 'Deal' }]}
        />
      </div>
      <div>
        <label className="label">Assignment Method</label>
        <DropdownSelect
          value={watch('method') ?? 'round_robin'}
          onChange={v => setValue('method', v)}
          options={[
            { value: 'round_robin', label: 'Round Robin' },
            { value: 'load_balance', label: 'Load Balance' },
            { value: 'specific_user', label: 'Specific User' },
          ]}
        />
      </div>
      <div><label className="label">Assignees (comma-separated user IDs)</label><input {...register('assignees')} className="input" placeholder="uuid1, uuid2" /></div>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Create Rule</button>
      </div>
    </form>
  )
}

// ─── SLA Policies ────────────────────────────────────────────────────────────
export function SlaPoliciesSettings() {
  usePageTitle('SLA Policies')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['sla-policies', companyId],
    queryFn: () => slaPoliciesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => slaPoliciesApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Policy deleted')
      qc.invalidateQueries({ queryKey: ['sla-policies', companyId] })
      setDeleteTarget(null)
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">SLA Policies</h2>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm"><Plus className="w-3.5 h-3.5" /> Add Policy</button>
      </div>
      <div className="card overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Applies To</th><th>Response</th><th>Resolution</th><th></th></tr></thead>
          <tbody>
            {(!data || data.length === 0) && <tr><td colSpan={5} className="text-center py-8 text-gray-500 text-sm">No SLA policies</td></tr>}
            {data?.map((p: { id: string; name: string; applyTo: string; responseMinutes: number; resolutionMinutes: number | null }) => (
              <tr key={p.id}>
                <td className="font-medium">{p.name}</td>
                <td><span className="badge badge-gray capitalize">{p.applyTo}</span></td>
                <td>{formatDurationMinutes(p.responseMinutes)}</td>
                <td>{p.resolutionMinutes != null ? formatDurationMinutes(p.resolutionMinutes) : '—'}</td>
                <td><button onClick={() => setDeleteTarget({ id: p.id, label: p.name })} className="btn btn-ghost btn-sm p-1 text-red-600"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add SLA Policy" size="sm">
        <SlaForm companyId={companyId!} onSuccess={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['sla-policies', companyId] }) }} />
      </Modal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete SLA policy?"
        message={`This will remove "${deleteTarget?.label}". Existing breach calculations stop applying immediately.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

function SlaForm({ companyId, onSuccess }: { companyId: string; onSuccess: () => void }) {
  const { register, handleSubmit, reset, watch: watchSla, setValue: setValueSla } = useForm<{ name: string; applyTo: string; responseMinutes: number; resolutionMinutes: number }>({
    defaultValues: { applyTo: 'lead', responseMinutes: 1440, resolutionMinutes: 4320 },
  })
  const mutation = useMutation({
    mutationFn: (dto: { name: string; applyTo: string; responseMinutes: number; resolutionMinutes: number }) =>
      // conditions = {} → policy applies to ALL records of applyTo. A
      // condition-tree builder UI is a future feature.
      slaPoliciesApi.create(companyId, { ...dto, conditions: {} }),
    onSuccess: () => { reset(); toast.success('Policy created'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to create SLA policy')
    },
  })
  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div><label className="label">Name *</label><input {...register('name', { required: true })} className="input" /></div>
      <div>
        <label className="label">Applies To</label>
        <DropdownSelect
          value={watchSla('applyTo') ?? 'lead'}
          onChange={v => setValueSla('applyTo', v)}
          options={[{ value: 'lead', label: 'Lead' }, { value: 'deal', label: 'Deal' }]}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Response (minutes)</label>
          <input {...register('responseMinutes', { valueAsNumber: true })} type="number" min="1" className="input" />
          <p className="text-xs text-gray-500 mt-1">e.g. 60 = 1h, 1440 = 24h</p>
        </div>
        <div>
          <label className="label">Resolution (minutes)</label>
          <input {...register('resolutionMinutes', { valueAsNumber: true })} type="number" min="1" className="input" />
          <p className="text-xs text-gray-500 mt-1">e.g. 4320 = 72h</p>
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Create Policy</button>
      </div>
    </form>
  )
}

// ─── Custom Fields ────────────────────────────────────────────────────────────
export function CustomFieldsSettings() {
  usePageTitle('Custom Fields')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['custom-fields', companyId],
    queryFn: () => customFieldsApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customFieldsApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Field deleted')
      qc.invalidateQueries({ queryKey: ['custom-fields', companyId] })
      setDeleteTarget(null)
    },
  })

  const { register, handleSubmit, reset, watch: watchCf, setValue: setValueCf } = useForm<{ applyTo: 'lead' | 'deal'; fieldKey: string; label: string; fieldType: string }>({
    defaultValues: { applyTo: 'lead', fieldType: 'text' },
  })
  const createMutation = useMutation({
    mutationFn: (dto: { applyTo: 'lead' | 'deal'; fieldKey: string; label: string; fieldType: string }) =>
      customFieldsApi.create(companyId!, dto),
    onSuccess: () => { reset(); toast.success('Field created'); qc.invalidateQueries({ queryKey: ['custom-fields', companyId] }) },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to create field')
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-gray-900">Custom Fields</h2>
      <div className="card p-4 space-y-3">
        <p className="text-sm font-medium text-gray-700">Add Field</p>
        <form onSubmit={handleSubmit(d => createMutation.mutate(d))} className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="label text-xs">Apply To</label>
            <DropdownSelect
              value={watchCf('applyTo') ?? 'lead'}
              onChange={v => setValueCf('applyTo', v as 'lead' | 'deal')}
              options={[{ value: 'lead', label: 'Lead' }, { value: 'deal', label: 'Deal' }]}
              className="w-28"
            />
          </div>
          <div>
            <label className="label text-xs">Field Key *</label>
            <input {...register('fieldKey', { required: true })} placeholder="e.g. budget" className="input" />
          </div>
          <div>
            <label className="label text-xs">Label *</label>
            <input {...register('label', { required: true })} placeholder="e.g. Budget" className="input" />
          </div>
          <div>
            <label className="label text-xs">Type</label>
            <DropdownSelect
              value={watchCf('fieldType') ?? 'text'}
              onChange={v => setValueCf('fieldType', v)}
              options={[
                { value: 'text', label: 'Text' },
                { value: 'number', label: 'Number' },
                { value: 'date', label: 'Date' },
                { value: 'boolean', label: 'Boolean' },
                { value: 'select', label: 'Select' },
              ]}
              className="w-32"
            />
          </div>
          <button type="submit" disabled={createMutation.isPending} className="btn btn-primary btn-sm h-9">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </form>
      </div>
      <div className="card overflow-hidden">
        <table className="table">
          <thead><tr><th>Key</th><th>Label</th><th>Type</th><th>Applies To</th><th></th></tr></thead>
          <tbody>
            {(!data || data.length === 0) && <tr><td colSpan={5} className="text-center py-8 text-gray-500 text-sm">No custom fields</td></tr>}
            {data?.map((f: { id: string; fieldKey: string; label: string; fieldType: string; applyTo: string }) => (
              <tr key={f.id}>
                <td className="font-mono text-xs">{f.fieldKey}</td>
                <td>{f.label}</td>
                <td><span className="badge badge-gray">{f.fieldType}</span></td>
                <td><span className="badge badge-blue">{f.applyTo}</span></td>
                <td><button onClick={() => setDeleteTarget({ id: f.id, label: `${f.label} (${f.fieldKey})` })} className="btn btn-ghost btn-sm p-1 text-red-600"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete custom field?"
        message={`This will remove ${deleteTarget?.label}. Stored values on existing leads/deals are kept in the JSON column but no longer surfaced.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

// ─── Saved Views ──────────────────────────────────────────────────────────────
export function SavedViewsSettings() {
  usePageTitle('Saved Views')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['views', companyId],
    queryFn: () => savedViewsApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => savedViewsApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('View deleted')
      qc.invalidateQueries({ queryKey: ['views', companyId] })
      setDeleteTarget(null)
    },
  })

  const pinMutation = useMutation({
    mutationFn: (id: string) => savedViewsApi.pin(companyId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['views', companyId] }),
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-gray-900">Saved Views</h2>
      <div className="card overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Type</th><th>Visibility</th><th>Pinned</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {(!data || data.length === 0) && <tr><td colSpan={6} className="text-center py-8 text-gray-500 text-sm">No saved views</td></tr>}
            {data?.map((v: { id: string; name: string; refType: string; isPublic: boolean; isPinned: boolean; createdAt: string }) => (
              <tr key={v.id}>
                <td className="font-medium">{v.name}</td>
                <td><span className="badge badge-gray capitalize">{v.refType}</span></td>
                <td><span className={`badge ${v.isPublic ? 'badge-green' : 'badge-gray'}`}>{v.isPublic ? 'Public' : 'Private'}</span></td>
                <td>
                  <button onClick={() => pinMutation.mutate(v.id)} className={`text-xs ${v.isPinned ? 'text-yellow-600' : 'text-gray-400'}`}>
                    {v.isPinned ? '📌 Pinned' : 'Pin'}
                  </button>
                </td>
                <td className="text-gray-500 text-xs">{formatDate(v.createdAt)}</td>
                <td><button onClick={() => setDeleteTarget({ id: v.id, label: v.name })} className="btn btn-ghost btn-sm p-1 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete saved view?"
        message={`This will remove "${deleteTarget?.label}". Anyone with the view bookmarked will lose access.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
